package club.srams.website.pages

import androidx.compose.runtime.Composable
import club.srams.website.HeadlineTextStyle
import club.srams.website.components.layouts.PageLayoutData
import club.srams.website.toSitePalette
import com.varabyte.kobweb.compose.css.AlignContent
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 400.px

private val PRESS_IMAGE_MAX_HEIGHT = 800.px
private val PRESS_IMAGE_MAX_WIDTH = 900.px
private val PRESS_IMAGE_VERTICAL_MARGIN = 24.px

private val PROGRAM_POSTER_IMAGE_MAX_HEIGHT = 800.px
private val PROGRAM_POSTER_IMAGE_MAX_WIDTH = 400.px
private val PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN = 24.px

@InitRoute
fun initPebblePupsPageV2(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Pebble Pups v2"))
}

val PebblePupsHeadlineStyle = CssStyle {
    base { Modifier.fontSize(1.5.cssRem) }   // mobile
    Breakpoint.MD { Modifier.fontSize(2.5.cssRem) } // desktop
}

@Composable
private fun PebblePupsHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()
    val textColor = if (ColorMode.current == ColorMode.LIGHT) Colors.Black else Colors.White

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Pebble Pups for "
                , PebblePupsHeadlineStyle.toModifier().color(textColor)
            )
            SpanText(
                "Junior Rockhounds",
                PebblePupsHeadlineStyle.toModifier()
                    .color(sitePalette.brand.accent)
                    .textShadow(0.px, 0.px, blurRadius = 0.5.cssRem, color = Colors.Gray)
            )
        }
    }
}

@Composable
private fun PebblePupsHero() {
    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2),
        modifier = HeroContainerStyle.toModifier().fillMaxWidth().gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                PebblePupsHeadline()

                Image(
                    src = "/images/pebble_pups/check_before_you_collect.jpg",
                    alt = "how to identify rocks",
                    modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }
        Image(
            src = "/images/pebble_pups/PebblePups.png",
            alt = "Pebble Pups poster",
            modifier = Modifier
                .maxWidth(HERO_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
    }
}

@Composable
private fun PebblePupsPressSection() {
    // Single centered column — image scales with viewport on small screens
    SimpleGrid(
        numColumns = numColumns(1)
        , modifier = Modifier.fillMaxWidth()
    ) {
        Image(
            src = "/images/pebble_pups/2026_03_pebble_pups_press.jpg",
            alt = "Pebble Pups Press",
            modifier = Modifier
                .maxWidth(PRESS_IMAGE_MAX_WIDTH)
                .maxHeight(PRESS_IMAGE_MAX_HEIGHT)
                .fillMaxWidth()  // shrinks on small screens, caps at max on large
                .margin(top = PRESS_IMAGE_VERTICAL_MARGIN, bottom = PRESS_IMAGE_VERTICAL_MARGIN)
        )
    }
}

@Composable
private fun PebblePupsProgramsSection() {
    // Single centered column — image scales with viewport on small screens
    SimpleGrid(
        numColumns = numColumns(1)
        , modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            Modifier
                .fillMaxWidth()
            , horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Image(
                src = "/images/pebble_pups/2026-04-pebble-pups-poster.jpg"
                , alt = "2026/04 program"
                , modifier = Modifier
                    .maxWidth(PROGRAM_POSTER_IMAGE_MAX_WIDTH)
                    .maxHeight(PROGRAM_POSTER_IMAGE_MAX_HEIGHT)
                    .fillMaxWidth()  // shrinks on small screens, caps at max on large
                    .margin(
                        top = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                        , bottom = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                    )
            )

            Image(
                src = "/images/pebble_pups/2026-03-pebble-pups-poster.jpg"
                , alt = "2026/03 program"
                , modifier = Modifier
                    .maxWidth(PROGRAM_POSTER_IMAGE_MAX_WIDTH)
                    .maxHeight(PROGRAM_POSTER_IMAGE_MAX_HEIGHT)
                    .fillMaxWidth()  // shrinks on small screens, caps at max on large
                    .margin(
                        top = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                        , bottom = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                    )
            )

            Image(
                src = "/images/pebble_pups/2026-02-pebble-pups-poster.jpg"
                , alt = "2026/02 program"
                , modifier = Modifier
                    .maxWidth(PROGRAM_POSTER_IMAGE_MAX_WIDTH)
                    .maxHeight(PROGRAM_POSTER_IMAGE_MAX_HEIGHT)
                    .fillMaxWidth()  // shrinks on small screens, caps at max on large
                    .margin(
                        top = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                        , bottom = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                    )
            )

            Image(
                src = "/images/pebble_pups/2026-01-pebble-pups-poster.jpg"
                , alt = "2026/01 program"
                , modifier = Modifier
                    .maxWidth(PROGRAM_POSTER_IMAGE_MAX_WIDTH)
                    .maxHeight(PROGRAM_POSTER_IMAGE_MAX_HEIGHT)
                    .fillMaxWidth()  // shrinks on small screens, caps at max on large
                    .margin(
                        top = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                        , bottom = PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN
                    )
            )
        }
    }
}

@Page("pebble-pups-v2")
@Layout(".components.layouts.PageLayout")
@Composable
fun PebblePupsPage_v2() {
    PebblePupsHero()
    HorizontalDivider(Modifier.fillMaxWidth())
    PebblePupsPressSection()
    PebblePupsProgramsSection()
}